/**
 * Dashboard Event Service
 * Manages real-time dashboard updates and event coordination
 */

class DashboardEventService {
  constructor() {
    this.eventSource = null;
    this.subscribers = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.isConnected = false;
  }

  /**
   * Connect to Server-Sent Events for real-time updates
   */
  connect(token) {
    if (this.eventSource) {
      this.disconnect();
    }

    try {
      const url = `/api/events/dashboard?token=${encodeURIComponent(token)}`;
      this.eventSource = new EventSource(url);

      this.eventSource.onopen = () => {
        console.log('📡 Dashboard SSE connection established');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.notifySubscribers('connection', { status: 'connected' });
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleEvent(data.type, data.payload);
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      this.eventSource.onerror = (error) => {
        console.error('Dashboard SSE error:', error);
        this.isConnected = false;
        this.notifySubscribers('connection', { status: 'error', error });
        this.handleReconnection();
      };

      // Listen for specific event types
      this.setupEventListeners();
    } catch (error) {
      console.error('Failed to establish SSE connection:', error);
    }
  }

  /**
   * Set up listeners for specific dashboard events
   */
  setupEventListeners() {
    if (!this.eventSource) return;

    // Reservation events
    this.eventSource.addEventListener('reservation-created', (event) => {
      const data = JSON.parse(event.data);
      this.handleEvent('reservation-created', data);
    });

    this.eventSource.addEventListener('reservation-updated', (event) => {
      const data = JSON.parse(event.data);
      this.handleEvent('reservation-updated', data);
    });

    this.eventSource.addEventListener('reservation-cancelled', (event) => {
      const data = JSON.parse(event.data);
      this.handleEvent('reservation-cancelled', data);
    });

    // Inventory events
    this.eventSource.addEventListener('inventory-status-changed', (event) => {
      const data = JSON.parse(event.data);
      this.handleEvent('inventory-status-changed', data);
    });

    // Payment events
    this.eventSource.addEventListener('payment-processed', (event) => {
      const data = JSON.parse(event.data);
      this.handleEvent('payment-processed', data);
    });

    // System events
    this.eventSource.addEventListener('stats-updated', (event) => {
      const data = JSON.parse(event.data);
      this.handleEvent('stats-updated', data);
    });
  }

  /**
   * Handle incoming events
   */
  handleEvent(type, payload) {
    console.log(`📡 Dashboard event received: ${type}`, payload);
    this.notifySubscribers(type, payload);
  }

  /**
   * Subscribe to dashboard events
   */
  subscribe(eventType, callback) {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set());
    }
    
    this.subscribers.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.subscribers.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  /**
   * Notify all subscribers of an event
   */
  notifySubscribers(eventType, payload) {
    const callbacks = this.subscribers.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`Error in dashboard event callback for ${eventType}:`, error);
        }
      });
    }

    // Also notify 'all' subscribers
    const allCallbacks = this.subscribers.get('all');
    if (allCallbacks) {
      allCallbacks.forEach(callback => {
        try {
          callback(eventType, payload);
        } catch (error) {
          console.error('Error in dashboard catch-all event callback:', error);
        }
      });
    }
  }

  /**
   * Handle reconnection logic
   */
  handleReconnection() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached for dashboard SSE');
      this.notifySubscribers('connection', { status: 'disconnected', maxAttemptsReached: true });
      return;
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    setTimeout(() => {
      console.log(`Attempting to reconnect dashboard SSE (attempt ${this.reconnectAttempts})`);
      // Only notify about reconnection once, not continuously
      if (this.reconnectAttempts === 1) {
        this.notifySubscribers('reconnection', { 
          attempt: this.reconnectAttempts, 
          maxAttempts: this.maxReconnectAttempts 
        });
      }
    }, delay);
  }

  /**
   * Disconnect from SSE
   */
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.isConnected = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      connected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }

  /**
   * Clear all subscribers
   */
  clearSubscribers() {
    this.subscribers.clear();
  }

  /**
   * Emit a custom dashboard event (for testing or local events)
   */
  emit(eventType, payload) {
    this.notifySubscribers(eventType, payload);
  }
}

const dashboardEventService = new DashboardEventService();
export default dashboardEventService;